<script setup>
import { ref, watch, computed, onMounted, onUnmounted } from 'vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import OrderIssueItem from 'dashboard/components/widgets/conversation/OrderIssueItem.vue';
import OrderIssueModal from 'dashboard/routes/dashboard/orderIssues/OrderIssueModal.vue';
import orderIssuesAPI from 'dashboard/api/orderIssues';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  contactId: {
    type: [Number, String],
    default: null,
  },
  conversationId: {
    type: [Number, String],
    default: null,
  },
  conversationDisplayId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(['close', 'updated']);

const popoverRef = ref(null);

const issues = ref([]);
const loading = ref(true);
const error = ref('');
const showCreateModal = ref(false);

const fetchIssues = async () => {
  if (!props.contactId) {
    loading.value = false;
    return;
  }
  
  try {
    loading.value = true;
    error.value = '';
    const response = await orderIssuesAPI.getByContact(props.contactId);
    issues.value = response.data || [];
  } catch (e) {
    error.value = 'Failed to load order issues';
    issues.value = [];
  } finally {
    loading.value = false;
  }
};

const activeIssuesCount = computed(() => {
  return issues.value.filter(i => !i.done && !i.hidden).length;
});

const openCreateModal = () => {
  showCreateModal.value = true;
};

const handleCreateSuccess = () => {
  showCreateModal.value = false;
  fetchIssues();
  emit('updated');
  // Notify other components (sidebar) to refresh
  emitter.emit(BUS_EVENTS.ORDER_ISSUES_UPDATED, { contactId: props.contactId });
};

const handleIssueUpdated = () => {
  fetchIssues();
  emit('updated');
  // Notify other components (sidebar) to refresh
  emitter.emit(BUS_EVENTS.ORDER_ISSUES_UPDATED, { contactId: props.contactId });
};

// Listen for updates from other components (sidebar)
const onOrderIssuesUpdated = (data) => {
  // Only refresh if it's for the same contact
  if (data?.contactId === props.contactId) {
    fetchIssues();
  }
};

// Close on click outside
const handleClickOutside = (event) => {
  if (popoverRef.value && !popoverRef.value.contains(event.target)) {
    emit('close');
  }
};

watch(() => props.show, (newVal) => {
  if (newVal) {
    fetchIssues();
    setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 100);
  } else {
    document.removeEventListener('click', handleClickOutside);
  }
});

onMounted(() => {
  emitter.on(BUS_EVENTS.ORDER_ISSUES_UPDATED, onOrderIssuesUpdated);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  emitter.off(BUS_EVENTS.ORDER_ISSUES_UPDATED, onOrderIssuesUpdated);
});
</script>

<template>
  <div v-if="show" ref="popoverRef" class="absolute bottom-full left-0 mb-2 z-50">
    <div class="bg-n-solid-2 border border-n-weak rounded-xl shadow-xl w-[340px] max-h-[480px] overflow-hidden">
      <!-- Header -->
      <div class="flex items-center justify-between px-4 py-3 border-b border-n-weak">
        <h4 class="text-sm font-semibold text-n-slate-12 flex items-center gap-2">
          <i class="i-lucide-alert-circle text-n-amber-10" />
          Order Issues
          <span v-if="activeIssuesCount" class="text-xs bg-n-ruby-9 text-white px-1.5 py-0.5 rounded-full">
            {{ activeIssuesCount }}
          </span>
        </h4>
        <button class="text-n-slate-11 hover:text-n-slate-12" @click="emit('close')">
          <i class="i-lucide-x text-sm" />
        </button>
      </div>

      <!-- Content -->
      <div class="max-h-[360px] overflow-y-auto px-4">
        <!-- Loading -->
        <div v-if="loading" class="flex justify-center items-center p-6">
          <Spinner size="24" class="text-n-brand" />
        </div>

        <!-- Error -->
        <div v-else-if="error" class="text-center text-n-ruby-11 p-4 text-sm">
          {{ error }}
        </div>

        <!-- Issues List - Using the same OrderIssueItem component as sidebar -->
        <template v-else>
          <div v-if="!issues.length" class="text-center text-n-slate-11 py-6 text-sm">
            No order issues for this contact
          </div>

          <div v-else>
            <OrderIssueItem
              v-for="issue in issues"
              :key="issue.id"
              :issue="issue"
              @updated="handleIssueUpdated"
            />
          </div>
        </template>
      </div>

      <!-- Footer - Create button -->
      <div class="px-4 py-3 border-t border-n-weak">
        <Button
          variant="solid"
          color="blue"
          size="sm"
          icon="i-lucide-plus"
          class="w-full"
          @click="openCreateModal"
        >
          + Log New Order Issue
        </Button>
      </div>
    </div>

    <!-- Order Issue Modal for CREATE ONLY - createMode forces new issue creation -->
    <OrderIssueModal
      :show="showCreateModal"
      :conversation-id="conversationId"
      :conversation-display-id="conversationDisplayId"
      :create-mode="true"
      @close="showCreateModal = false"
      @success="handleCreateSuccess"
    />
  </div>
</template>
